import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { capitalizeFirstLetter } from '../../utils/helpers';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

export default function ChatDialog({
  open,
  onClose,
  messages,
  userId,
  friendId,
  friendName,
  onDeleteMessage,
  onClearHistory,
}) {
  const handleDeleteMessage = async (msgId) => {
    const result = await Swal.fire({
      title: 'Delete this message?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      onDeleteMessage(msgId);
      toast.success('Message deleted');
    }
  };

  const handleClearHistory = async () => {
    const result = await Swal.fire({
      title: 'Clear chat history?',
      text: 'All messages will be deleted permanently.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, clear it!',
    });

    if (result.isConfirmed) {
      onClearHistory(userId, friendId);
      toast.success('Chat history cleared');
    }
  };

  const base64ToBlobUrl = (base64Data, mimeType = "audio/wav") => {
    if (!base64Data) return null;
    // Remove data url prefix if present
    const cleanedBase64 = base64Data.replace(/^data:audio\/[a-zA-Z]+;base64,/, "");
    // Replace url-safe base64 chars
    let base64 = cleanedBase64.replace(/-/g, "+").replace(/_/g, "/");

    // Pad base64 string if needed
    const padding = base64.length % 4;
    if (padding === 2) base64 += "==";
    else if (padding === 3) base64 += "=";
    else if (padding !== 0) {
      console.error("Invalid base64 string length");
      return null;
    }

    try {
      const byteString = window.atob(base64);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const intArray = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([intArray], { type: mimeType });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Failed to decode base64 audio", error);
      return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ position: 'relative' }}>
        Chat with: {friendName || 'Friend'}

        {messages && messages.length > 0 && (
          <IconButton
            onClick={handleClearHistory}
            sx={{ position: 'absolute', right: 48, top: 8 }}
          >
            <DeleteIcon />
          </IconButton>
        )}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>


      <DialogContent
        dividers
        sx={{
          maxHeight: 400,
          overflowY: 'auto',
        }}
      >
        {messages && messages.length > 0 ? (
          <Stack spacing={3}>
            {messages.map((msg) => {
              const isSender = msg?.senderId?._id === userId;
              const senderName = isSender
                ? capitalizeFirstLetter(msg?.senderId?.username) || 'You'
                : capitalizeFirstLetter(msg?.senderId?.username) || 'Friend';

              return (
                <Box
                  key={msg._id}
                  display="flex"
                  flexDirection="column"
                  alignItems={isSender ? 'flex-end' : 'flex-start'}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'gray', mb: 0.5, textAlign: isSender ? 'right' : 'left' }}
                  >
                    {senderName}
                  </Typography>

                  <Box display="flex" alignItems="center" flexDirection={isSender ? 'row-reverse' : 'row'}>
                    <Box
                      sx={{
                        bgcolor: isSender ? 'rgb(53 162 222)' : '#e0e0e0',
                        color: isSender ? '#fff' : '#000',
                        p: 1.5,
                        borderRadius: 2,
                        maxWidth: '75%',
                      }}
                    >
                      <Typography variant="body2">{
                        msg.type === "audio" ? (
                          <audio controls src={base64ToBlobUrl(msg.message, "audio/wav")} style={{ width: 200 }} />
                        ) : (
                          msg.message
                        )
                      }</Typography>
                    </Box>

                    <IconButton
                      onClick={() => handleDeleteMessage(msg._id)}
                      size="small"
                      sx={{
                        ml: isSender ? 1 : 0,
                        mr: isSender ? 0 : 1,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="textSecondary" textAlign="center" mt={2}>
            No messages.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
